import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { OutletContextType } from "../../types/GlobalType";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  domainController,
  functionController,
  gradeController,
} from "../../_api/controllers/scolarite.controller.ts";
import { personalController } from "../../_api/controllers/personal.controller.ts";
import { userController } from "../../_api/controllers/user.controller.ts";
import { capitalizeFirstLetter } from "../../_services/utils";
import { createPersonalSchema } from "../../validators/personal.schema.ts";
import z from "zod";
import moment from "moment";

// personal skeleton component
const PersonalsPageSkeleton = ({
  isDarkMode,
  rows = 6,
}: {
  isDarkMode: boolean;
  rows?: number;
}) => {
  const bgCard = isDarkMode ? "bg-[#161B22]" : "bg-slate-50";
  const bgPage = isDarkMode ? "bg-[#0D1117]" : "bg-white";
  const border = isDarkMode ? "border-[#30363D]" : "border-gray-200";
  const skeleton = "bg-gray-300 dark:bg-gray-700";

  return (
    <div className="space-y-4 animate-pulse">
      <div className={`rounded-2xl overflow-hidden border shadow-sm ${border}`}>
        <div className={`p-4 sm:p-5 ${bgPage}`}>
          {/* ===== STATS ===== */}
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className={`p-3.5 sm:p-4 rounded-xl border ${bgCard} ${border}`}
              >
                <div className="flex justify-between items-center">
                  <div className="space-y-2">
                    <div className={`h-3 w-28 rounded ${skeleton}`} />
                    <div className={`h-7 w-20 rounded ${skeleton}`} />
                  </div>
                  <div className={`h-12 w-12 rounded-full ${skeleton}`} />
                </div>
              </div>
            ))}
          </div>

          {/* ===== SEARCH & FILTER ===== */}
          <div className="flex flex-wrap gap-2.5 mb-5">
            <div className={`h-10 flex-grow rounded ${skeleton}`} />
            <div className={`h-10 w-40 rounded ${skeleton}`} />
            <div className={`h-10 w-48 rounded ${skeleton}`} />
          </div>

          {/* ===== TABLE ===== */}
          <div
            className={`rounded-2xl overflow-hidden border shadow-sm ${border}`}
          >
            {/* Table header */}
            <div
              className={`grid grid-cols-5 gap-4 px-6 py-3 ${
                isDarkMode ? "bg-[#161B22]" : "bg-slate-50"
              }`}
            >
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className={`h-3 rounded ${skeleton}`} />
              ))}
            </div>

            {/* Rows */}
            <div className={`${bgPage}`}>
              {[...Array(rows)].map((_, i) => (
                <div
                  key={i}
                  className={`grid grid-cols-5 gap-4 px-6 py-4 border-t ${border}`}
                >
                  {/* Name */}
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-full ${skeleton}`} />
                    <div className="space-y-2">
                      <div className={`h-3 w-32 rounded ${skeleton}`} />
                      <div className={`h-2 w-20 rounded ${skeleton}`} />
                    </div>
                  </div>

                  <div className={`h-3 w-24 rounded ${skeleton}`} />
                  <div className={`h-5 w-20 rounded-full ${skeleton}`} />
                  <div className={`h-5 w-16 rounded-full ${skeleton}`} />

                  {/* Actions */}
                  <div className="flex justify-center gap-3">
                    <div className={`h-5 w-5 rounded ${skeleton}`} />
                    <div className={`h-5 w-5 rounded ${skeleton}`} />
                    <div className={`h-5 w-5 rounded ${skeleton}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ===== PAGINATION ===== */}
          <div className="mt-4">
            <div className={`h-3 w-48 rounded ${skeleton}`} />
          </div>
        </div>
      </div>
    </div>
  );
};

// Ligne d'information en lecture seule pour le popup de détail
const DetailField = ({
  label,
  value,
  isDarkMode,
  icon,
}: {
  label: string;
  value: React.ReactNode;
  isDarkMode: boolean;
  icon?: string;
}) => (
  <div>
    <p
      className={`text-xs font-medium mb-0.5 ${
        isDarkMode ? "text-gray-500" : "text-gray-400"
      }`}
    >
      {label}
    </p>
    <p
      className={`text-sm flex items-center gap-1.5 ${
        isDarkMode ? "text-gray-100" : "text-gray-900"
      }`}
    >
      {icon && <i className={`fas ${icon} text-xs opacity-60`}></i>}
      {value || "-"}
    </p>
  </div>
);

const Personal: React.FC = () => {
  const { isDarkMode, setIsDarkMode } = useOutletContext<OutletContextType>();
  const [notification, setNotification] = useState<any>(null); // { message, type }
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState<any>(false); // { message, type }
  const showNotification = (message: string, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Detail popup (informations du personnel)
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedPersonal, setSelectedPersonal] = useState<any>(null);

  const handleRowClick = (item: any) => {
    setSelectedPersonal(item);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedPersonal(null);
  };

  //filter personal
  const [searchQuery, setSearchQuery] = useState("");
  const [filtrerPersonalStatus, setfiltrerPersonalStatus] =
    useState<any>("Tous les status");
  const [isPersonalStatusDropdownOpen, setIsPersonalStatusDropdownOpen] =
    useState(false);
  const toggleStatusDropdown = () => {
    setIsPersonalStatusDropdownOpen(!isPersonalStatusDropdownOpen);
  };
  const personalStatusDropdownRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      //status button
      if (
        personalStatusDropdownRef.current &&
        !personalStatusDropdownRef.current.contains(event.target as Node)
      ) {
        setIsPersonalStatusDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [formEditData, setFormEditData] = useState<{
    id: any;
    nom: string;
    postnom: string;
    prenom: string;
    genre: string;
    ville: string;
    commune: string;
    quartier: string;
    avenue: string;
    telephone: string;
    uid: string;
    fonction_id: any;
    grade_id: any;
    date_naissance: string;
    lieu_naissance: string;
    domaine_id: any;
    email: string;
  }>({
    id: "",
    nom: "",
    postnom: "",
    prenom: "",
    genre: "",
    ville: "",
    commune: "",
    quartier: "",
    avenue: "",
    telephone: "",
    uid: "12345688",
    fonction_id: "",
    grade_id: "",
    date_naissance: "",
    lieu_naissance: "",
    domaine_id: "",
    email: "",
  });
  const [formNewData, setFormNewData] = useState<{
    nom: string;
    postnom: string;
    prenom: string;
    genre: string;
    ville: string;
    commune: string;
    quartier: string;
    avenue: string;
    telephone: string;
    uid: string;
    fonction_id: any;
    grade_id: any;
    date_naissance: string;
    lieu_naissance: string;
    domaine_id: any;
    email: string;
  }>({
    nom: "",
    postnom: "",
    prenom: "",
    genre: "",
    ville: "",
    commune: "",
    quartier: "",
    avenue: "",
    telephone: "",
    uid: "12345688",
    fonction_id: "",
    grade_id: "",
    date_naissance: "",
    lieu_naissance: "",
    domaine_id: "",
    email: "",
  });

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    showEditModal
      ? setFormEditData((prev) => ({ ...prev, [name]: value }))
      : setFormNewData((prev) => ({ ...prev, [name]: value }));
  };

  // Pré-remplit et ouvre le formulaire d'édition. Réutilisée par le bouton
  // "éditer" de la ligne ET par le popup de détail (même logique, aucun
  // nouveau setter introduit).
  const openEditForPersonal = (item: any) => {
    setFormEditData({
      id: item.personnel_id,
      nom: item.nom,
      postnom: item.postnom,
      prenom: item.prenom,
      genre: item.sexe,
      ville: item.ville,
      commune: item.commune,
      quartier: item.quartier,
      avenue: item.avenue,
      telephone: item.telephone,
      uid: "12345688",
      fonction_id: item.fonction_id,
      grade_id: item.grade_id,
      date_naissance: item.date_naissance,
      lieu_naissance: item.lieu_naissance,
      domaine_id: item.domaine_id,
      email: item.email,
    });
    setShowEditModal(true);
  };

  //UseReducer for Desactivate, Activate
  //handleling delete & activate & desactivate items
  interface ActionState {
    actionSection: string;
    actionType: string;
    actionText: string;
    currentId: any;
  }

  interface Action {
    type: "DELETE" | "DESACTIVATE" | "CANCEL" | "ACTIVATE";
    payload: { actionText: string; actionSection: string; currentId: any };
  }
  const ACTION_TYPES = {
    DELETE: "DELETE",
    DESACTIVATE: "DESACTIVATE",
    ACTIVATE: "ACTIVATE",
    CANCEL: "CANCEL",
  };

  const INITIAL_STATE: ActionState = {
    actionSection: "",
    actionType: "",
    actionText: "",
    currentId: null,
  };

  const actionReducer = (state: ActionState, action: Action): ActionState => {
    switch (action.type) {
      case ACTION_TYPES.DELETE:
        return {
          ...state,
          actionType: "delete",
          actionSection: action.payload.actionSection,
          actionText: action.payload.actionText,
          currentId: action.payload.currentId,
        };

      case ACTION_TYPES.ACTIVATE:
        return {
          ...state,
          actionType: "activate",
          actionSection: action.payload.actionSection,
          actionText: action.payload.actionText,
          currentId: action.payload.currentId,
        };
      case ACTION_TYPES.DESACTIVATE:
        return {
          ...state,
          actionType: "desactivate",
          actionSection: action.payload.actionSection,
          actionText: action.payload.actionText,
          currentId: action.payload.currentId,
        };
      case ACTION_TYPES.CANCEL:
        return {
          actionSection: "",
          actionType: "",
          actionText: "",
          currentId: null,
        };

      default:
        return state;
    }
  };

  const [state_actions, dispatch] = React.useReducer(
    actionReducer,
    INITIAL_STATE,
  );

  const queryClient = useQueryClient();

  //fetch all users
  const { data: personalData, isLoading: isPersonalLoading } = useQuery({
    queryKey: ["personals"],
    queryFn: personalController.getAll,
  });

  //fetch all personals
  const { data: usersData, isLoading: isUsersLoading } = useQuery({
    queryKey: ["users"],
    queryFn: userController.getAll,
  });

  //fetch all domains
  const { data: domainData, isLoading: isDomainLoading } = useQuery({
    queryKey: ["domains"],
    queryFn: domainController.getAll,
  });

  //fetch all functions
  const { data: functionData, isLoading: isFunctionLoading } = useQuery({
    queryKey: ["functions"],
    queryFn: functionController.getAll,
  });
  //fetch all grade
  const { data: gradeData, isLoading: isGradeLoading } = useQuery({
    queryKey: ["grades"],
    queryFn: gradeController.getAll,
  });

  const personalDataArr = personalData?.data || [];
  const usersDataArr = usersData?.data || [];
  const domainDataArr = domainData?.data || [];
  const functionDataArr = functionData?.data || [];
  const gradeDataArr = gradeData?.data || [];

  const updatePersonnalDataArr = personalDataArr.map((personal) => {
    let fonctionDesignation: string = "",
      gradeDesignation: string = "",
      domaineDesignation: string = "",
      email: string = "",
      userAccount: boolean = false;

    for (let item of domainDataArr) {
      if (personal.domaine_id === item.id) {
        domaineDesignation = item.designation;
      }
    }
    for (let item of functionDataArr) {
      if (personal.fonction_id === item.id) {
        fonctionDesignation = item.designation;
      }
    }
    for (let item of gradeDataArr) {
      if (personal.grade_id === item.id) {
        gradeDesignation = item.designation;
      }
    }
    for (let item of usersDataArr) {
      if (!!item.status && personal.personnel_id === item.personnel_id) {
        userAccount = true;
        email = item.email;
      }
    }
    return {
      ...personal,
      personalAccount: userAccount,
      fonctionDesignation,
      gradeDesignation,
      domaineDesignation,
    };
  });

  //statistic number
  let studentStats = {
    total: updatePersonnalDataArr.length,
    active: updatePersonnalDataArr.filter((item) => item.status).length,
    inactive: updatePersonnalDataArr.filter((item) => !item.status).length,
    systemAccount: updatePersonnalDataArr.filter((item) => item.personalAccount)
      .length,
  };

  const filteredPersonals = updatePersonnalDataArr.filter((personal) => {
    const matchesQuery =
      personal.nom.toLowerCase().includes(searchQuery.toLowerCase()) ||
      personal.postnom.toLowerCase().includes(searchQuery.toLowerCase()) ||
      personal.prenom.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStudentStatus =
      filtrerPersonalStatus === "Tous les status" ||
      filtrerPersonalStatus == personal.status;

    return matchesQuery && matchesStudentStatus;
  });

  const mutationDesactivatePersonal = useMutation({
    mutationFn: () => personalController.desactivate(state_actions.currentId),

    onSuccess: (data) => {
      // console.log(data);
      showNotification("Désactivation effectuée avec succès", "success");
      dispatch({
        type: "CANCEL",
        payload: { actionText: "", currentId: null, actionSection: "" },
      });
      // setCurrentId(null);
      queryClient.invalidateQueries({ queryKey: ["personals"] });
    },

    onError: (err) => {
      // console.log(err);
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationActivatePersonal = useMutation({
    mutationFn: () => personalController.activate(state_actions.currentId),

    onSuccess: (data) => {
      showNotification("Activation effectuée avec succès", "success");
      dispatch({
        type: "CANCEL",
        payload: { actionText: "", currentId: null, actionSection: "" },
      });
      // setCurrentId(null);
      queryClient.invalidateQueries({ queryKey: ["personals"] });
    },

    onError: (err: any) => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const mutationUpdatePersonal = useMutation({
    mutationFn: () =>
      personalController.update(formEditData.id, {
        nom: formEditData.nom,
        postnom: formEditData.postnom,
        prenom: formEditData.prenom,
        sexe: formEditData.genre,
        ville: formEditData.ville,
        commune: formEditData.commune,
        quartier: formEditData.quartier,
        avenue: formEditData.avenue,
        telephone: formEditData.telephone,
        uid: "12345688",
        fonction_id: formEditData.fonction_id,
        grade_id: formEditData.grade_id,
        date_naissance: formEditData.date_naissance,
        lieu_naissance: formEditData.lieu_naissance,
        domaine_id: formEditData.domaine_id,
        email: formEditData.email,
      }),

    onSuccess: () => {
      setShowEditModal(false);
      showNotification("Modification effectuée avec succès", "success");
      setFormEditData({
        id: "",
        nom: "",
        postnom: "",
        prenom: "",
        genre: "",
        date_naissance: "",
        lieu_naissance: "",
        ville: "",
        commune: "",
        quartier: "",
        avenue: "",
        telephone: "",
        uid: "12345678",
        fonction_id: "",
        grade_id: "",
        domaine_id: "",
        email: "",
      });
      queryClient.invalidateQueries({ queryKey: ["personals"] });
    },

    onError: () => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });
  const mutationCreatePersonal = useMutation({
    mutationFn: () =>
      personalController.create({
        nom: formNewData.nom,
        postnom: formNewData.postnom,
        prenom: formNewData.prenom,
        sexe: formNewData.genre,
        ville: formNewData.ville,
        commune: formNewData.commune,
        quartier: formNewData.quartier,
        avenue: formNewData.avenue,
        telephone: formNewData.telephone,
        uid: "12345688",
        fonction_id: formNewData.fonction_id,
        grade_id: formNewData.grade_id,
        date_naissance: formNewData.date_naissance,
        lieu_naissance: formNewData.lieu_naissance,
        domaine_id: formNewData.domaine_id,
        email: formNewData.email,
      }),

    onSuccess: () => {
      setShowAddModal(false);
      showNotification("Modification effectuée avec succès", "success");
      setFormNewData({
        nom: "",
        postnom: "",
        prenom: "",
        genre: "",
        date_naissance: "",
        lieu_naissance: "",
        ville: "",
        commune: "",
        quartier: "",
        avenue: "",
        telephone: "",
        uid: "12345678",
        fonction_id: "",
        grade_id: "",
        domaine_id: "",
        email: "",
      });
      queryClient.invalidateQueries({ queryKey: ["personals"] });
    },

    onError: () => {
      showNotification("Une erreur est survenue, réessayer....", "error");
    },
  });

  const handleActivate = () => {
    if (
      state_actions.actionType === "activate" &&
      state_actions.actionSection === "personal"
    ) {
      mutationActivatePersonal.mutate();
    }
  };

  const handleDesactivate = () => {
    if (
      state_actions.actionType === "desactivate" &&
      state_actions.actionSection === "personal"
    ) {
      mutationDesactivatePersonal.mutate();
    }
  };

  const handleUpdatePersonal = () => {
    const zodValidation = createPersonalSchema.safeParse(formEditData);

    if (!zodValidation.success) {
      const z_errors = z.treeifyError(zodValidation.error);

      return showNotification(
        z_errors
          ? (z_errors.properties?.nom?.errors[0] ??
              z_errors.properties?.postnom?.errors[0] ??
              z_errors.properties?.prenom?.errors[0] ??
              z_errors.properties?.genre?.errors[0] ??
              z_errors.properties?.lieu_naissance?.errors[0] ??
              z_errors.properties?.date_naissance?.errors[0] ??
              z_errors.properties?.ville?.errors[0] ??
              z_errors.properties?.commune?.errors[0] ??
              z_errors.properties?.quartier?.errors[0] ??
              z_errors.properties?.avenue?.errors[0] ??
              z_errors.properties?.email?.errors[0] ??
              z_errors.properties?.telephone?.errors[0] ??
              z_errors.properties?.fonction_id?.errors[0] ??
              z_errors.properties?.domaine_id?.errors[0] ??
              z_errors.properties?.grade_id?.errors[0]) ||
              "Erreur de validation"
          : "Erreur de validation",
        "erreur",
      );
    }

    mutationUpdatePersonal.mutate();
  };
  const handleCreatePersonal = () => {
    const zodValidation = createPersonalSchema.safeParse(formNewData);

    if (!zodValidation.success) {
      const z_errors = z.treeifyError(zodValidation.error);

      return showNotification(
        z_errors
          ? (z_errors.properties?.nom?.errors[0] ??
              z_errors.properties?.postnom?.errors[0] ??
              z_errors.properties?.prenom?.errors[0] ??
              z_errors.properties?.genre?.errors[0] ??
              z_errors.properties?.lieu_naissance?.errors[0] ??
              z_errors.properties?.date_naissance?.errors[0] ??
              z_errors.properties?.ville?.errors[0] ??
              z_errors.properties?.commune?.errors[0] ??
              z_errors.properties?.quartier?.errors[0] ??
              z_errors.properties?.avenue?.errors[0] ??
              z_errors.properties?.email?.errors[0] ??
              z_errors.properties?.telephone?.errors[0] ??
              z_errors.properties?.fonction_id?.errors[0] ??
              z_errors.properties?.domaine_id?.errors[0] ??
              z_errors.properties?.grade_id?.errors[0]) ||
              "Erreur de validation"
          : "Erreur de validation",
        "erreur",
      );
    }

    mutationCreatePersonal.mutate();
  };
  return (
    <>
      {isPersonalLoading ||
      isUsersLoading ||
      isDomainLoading ||
      isFunctionLoading ||
      isGradeLoading ? (
        <PersonalsPageSkeleton isDarkMode={isDarkMode} rows={6} />
      ) : (
        <div className="min-w-0 w-full space-y-4">
          <div
            className={`rounded-2xl overflow-hidden border shadow-sm ${
              isDarkMode ? "border-[#30363D]" : "border-gray-200"
            }`}
          >
            <div
              className={`p-4 sm:p-5 ${isDarkMode ? "bg-[#0D1117] text-gray-100" : "bg-white text-gray-900"}`}
            >
              {/* Stats Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
                <div
                  className={`p-3.5 sm:p-4 rounded-xl transition-all hover:shadow-sm ${
                    isDarkMode ? "bg-[#161B22]" : "bg-white"
                  } border ${
                    isDarkMode ? "border-[#30363D]" : "border-gray-200"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p
                        className={`text-[11px] font-medium ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        Total Personnel
                      </p>
                      <h3 className="text-xl font-bold mt-1 tracking-tight">
                        {studentStats.total}
                      </h3>
                    </div>
                    <div
                      className={`h-9 w-9 flex items-center justify-center rounded-lg ${
                        isDarkMode ? "bg-blue-500/10" : "bg-blue-50"
                      }`}
                    >
                      <i
                        className={`fas fa-users ${
                          isDarkMode ? "text-blue-400" : "text-blue-600"
                        }`}
                      ></i>
                    </div>
                  </div>
                </div>
                <div
                  className={`p-3.5 sm:p-4 rounded-xl transition-all hover:shadow-sm ${
                    isDarkMode ? "bg-[#161B22]" : "bg-white"
                  } border ${
                    isDarkMode ? "border-[#30363D]" : "border-gray-200"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p
                        className={`text-[11px] font-medium ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        Actifs
                      </p>
                      <h3 className="text-xl font-bold mt-1 tracking-tight">
                        {studentStats.active}
                      </h3>
                    </div>
                    <div
                      className={`h-9 w-9 flex items-center justify-center rounded-lg ${
                        isDarkMode ? "bg-emerald-500/10" : "bg-emerald-50"
                      }`}
                    >
                      <i
                        className={`fas fa-user-check ${
                          isDarkMode ? "text-green-400" : "text-green-600"
                        }`}
                      ></i>
                    </div>
                  </div>
                </div>
                <div
                  className={`p-3.5 sm:p-4 rounded-xl transition-all hover:shadow-sm ${
                    isDarkMode ? "bg-[#161B22]" : "bg-slate-50"
                  } border ${
                    isDarkMode ? "border-[#30363D]" : "border-gray-200"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p
                        className={`text-[11px] font-medium ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        Inactifs
                      </p>
                      <h3 className="text-xl font-bold mt-1 tracking-tight">
                        {studentStats.inactive}
                      </h3>
                    </div>
                    <div
                      className={`h-9 w-9 flex items-center justify-center rounded-lg ${
                        isDarkMode ? "bg-amber-500/10" : "bg-amber-50"
                      }`}
                    >
                      <i
                        className={`fas fa-archive ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      ></i>
                    </div>
                  </div>
                </div>
                <div
                  className={`p-3.5 sm:p-4 rounded-xl transition-all hover:shadow-sm ${
                    isDarkMode ? "bg-[#161B22]" : "bg-slate-50"
                  } border ${
                    isDarkMode ? "border-[#30363D]" : "border-gray-200"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p
                        className={`text-[11px] font-medium ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        Avec compte
                      </p>
                      <h3 className="text-xl font-bold mt-1 tracking-tight">
                        {studentStats.systemAccount}
                      </h3>
                    </div>
                    <div
                      className={`h-9 w-9 flex items-center justify-center rounded-lg ${
                        isDarkMode ? "bg-purple-500/10" : "bg-purple-100"
                      }`}
                    >
                      <i
                        className={`fas fa-user-shield ${
                          isDarkMode ? "text-purple-400" : "text-purple-600"
                        }`}
                      ></i>
                    </div>
                  </div>
                </div>
              </div>
              {/* Search and Filters */}
              <div className="flex flex-wrap items-center gap-2.5 mb-5">
                <div className="flex-grow relative min-w-[220px]">
                  <div
                    className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none ${
                      isDarkMode ? "text-gray-500" : "text-gray-400"
                    }`}
                  >
                    <i className="fas fa-search"></i>
                  </div>
                  <input
                    type="text"
                    placeholder="Rechercher un membre du personnel..."
                    className={`w-full h-10 pl-10 pr-4 rounded-lg text-xs transition-colors ${
                      isDarkMode
                        ? "bg-[#161B22] border-gray-700 text-gray-100"
                        : "bg-white border-gray-300 text-gray-900"
                    } border focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="relative">
                  <button
                    onClick={toggleStatusDropdown}
                    className={`h-10 inline-flex items-center justify-center gap-2 px-3.5 rounded-lg text-xs transition-colors ${
                      isDarkMode
                        ? "bg-[#161B22] hover:bg-gray-700"
                        : "bg-white hover:bg-gray-50"
                    } border ${
                      isDarkMode ? "border-[#30363D]" : "border-gray-200"
                    } !rounded-button whitespace-nowrap`}
                  >
                    <span>
                      {filtrerPersonalStatus == 1
                        ? "Actif"
                        : filtrerPersonalStatus == 0
                          ? "Inactif"
                          : "Tous les status"}
                    </span>
                    <i className="fas fa-chevron-down text-xs opacity-60"></i>
                  </button>
                  {isPersonalStatusDropdownOpen && (
                    <div
                      className={`absolute top-full left-0 mt-1.5 w-40 rounded-lg shadow-lg overflow-hidden ${
                        isDarkMode ? "bg-[#161B22]" : "bg-white"
                      } border ${
                        isDarkMode ? "border-[#30363D]" : "border-gray-200"
                      } z-50`}
                      ref={personalStatusDropdownRef}
                    >
                      <div className="py-1">
                        {[
                          {
                            id: 0,
                            designation: "Tous les status",
                            status: "Tous les status",
                          },
                          { id: 1, designation: "Actif", status: 1 },
                          { id: 2, designation: "Inactif", status: 0 },
                        ].map((status) => (
                          <button
                            key={status.id}
                            onClick={() => {
                              setfiltrerPersonalStatus(status.status);
                              setIsPersonalStatusDropdownOpen(false);
                            }}
                            className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                              isDarkMode
                                ? "hover:bg-gray-700 text-gray-200"
                                : "hover:bg-gray-100 text-gray-700"
                            } !rounded-button whitespace-nowrap`}
                          >
                            {status.designation}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setShowAddModal(true)}
                  className={`h-10 inline-flex items-center justify-center gap-2 px-3.5 rounded-lg text-xs font-medium transition-colors shadow-sm ${
                    isDarkMode
                      ? "bg-green-600 hover:bg-green-700"
                      : "bg-green-500 hover:bg-green-600"
                  } text-white !rounded-button whitespace-nowrap`}
                >
                  <i className="fas fa-plus"></i>Nouveau personnel
                </button>
              </div>
              {/* Personnel Table */}
              <div
                className={`rounded-xl w-full min-w-0 overflow-x-auto border ${
                  isDarkMode ? "border-[#30363D]" : "border-gray-200"
                }`}
              >
                <table
                  className={`w-full min-w-[920px] text-xs divide-y ${isDarkMode ? "divide-[#30363D]" : "divide-gray-200"}`}
                >
                  <thead
                    className={`${isDarkMode ? "bg-[#161B22]" : "bg-slate-50"}`}
                  >
                    <tr>
                      <th
                        scope="col"
                        className={`px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Nom & Prénom
                      </th>
                      <th
                        scope="col"
                        className={`px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Fonction
                      </th>
                      <th
                        scope="col"
                        className={`px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Grade
                      </th>
                      <th
                        scope="col"
                        className={`px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Compte système
                      </th>
                      <th
                        scope="col"
                        className={`px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Statut
                      </th>
                      <th
                        scope="col"
                        className={`px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-wider ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      ></th>
                    </tr>
                  </thead>
                  <tbody
                    className={`${
                      isDarkMode ? "bg-[#0D1117]" : "bg-white"
                    } divide-y ${
                      isDarkMode ? "divide-gray-700" : "divide-gray-200"
                    }`}
                  >
                    {filteredPersonals
                      .sort((a, b) =>
                        a.nom.toLowerCase().localeCompare(b.nom.toLowerCase()),
                      )
                      .map((item, index) => {
                        return (
                          <tr
                            key={item.id}
                            onClick={() => handleRowClick(item)}
                            className={`group cursor-pointer transition-colors ${
                              isDarkMode
                                ? "hover:bg-[#161B22]"
                                : "hover:bg-gray-50"
                            }`}
                          >
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-9 w-9">
                                  <div
                                    className={`h-9 w-9 rounded-xl flex items-center justify-center ${
                                      isDarkMode ? "bg-gray-700" : "bg-gray-200"
                                    }`}
                                  >
                                    <i className="fas fa-user"></i>
                                  </div>
                                </div>
                                <div className="ml-3">
                                  <span
                                    className={`px-0 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                      isDarkMode
                                        ? "text-gray-200"
                                        : "text-gray-700"
                                    }`}
                                  >
                                    {capitalizeFirstLetter(item.nom)}{" "}
                                    {capitalizeFirstLetter(item.postnom)}{" "}
                                    {capitalizeFirstLetter(item.prenom)}
                                  </span>{" "}
                                  <div
                                    className={`text-sm ${
                                      isDarkMode
                                        ? "text-gray-400"
                                        : "text-gray-500"
                                    }`}
                                  >
                                    ID: PRF-00{index + 1}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {item.fonctionDesignation}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {item.gradeDesignation}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span
                                className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  item.personalAccount
                                    ? isDarkMode
                                      ? "bg-green-900/40 text-green-400"
                                      : "bg-green-100 text-green-800"
                                    : isDarkMode
                                      ? "bg-red-900/40 text-red-400"
                                      : "bg-red-100 text-red-800"
                                } `}
                              >
                                {item.personalAccount ? "Oui" : "Non"}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span
                                className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  item.status
                                    ? isDarkMode
                                      ? "bg-blue-900/40 text-blue-400"
                                      : "bg-blue-100 text-blue-800"
                                    : isDarkMode
                                      ? "bg-red-900/40 text-red-400"
                                      : "bg-red-100 text-red-800"
                                } `}
                              >
                                {item.status ? "Actif" : "Inactif"}
                              </span>
                            </td>
                            <td className="w-[104px] min-w-[104px] px-4 py-3 whitespace-nowrap text-center">
                              <div className="flex items-center justify-center gap-1 opacity-0 pointer-events-none transition-opacity duration-150 group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto">
                                {/* <button className="text-blue-500 hover:text-blue-700 cursor-pointer !rounded-button whitespace-nowrap">
                            <i className="fas fa-eye"></i>
                          </button> */}
                                <button
                                  title="Modifier le personnel"
                                  aria-label="Modifier le personnel"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEditForPersonal(item);
                                  }}
                                  className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-green-500 hover:text-green-700 hover:bg-green-500/10 cursor-pointer focus-visible:ring-2 focus-visible:ring-blue-500"
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button
                                  title={
                                    item.status
                                      ? "Désactiver le personnel"
                                      : "Activer le personnel"
                                  }
                                  aria-label={
                                    item.status
                                      ? "Désactiver le personnel"
                                      : "Activer le personnel"
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    item.status
                                      ? dispatch({
                                          type: "DESACTIVATE",
                                          payload: {
                                            actionText:
                                              "Êtes-vous sûr de vouloir désactiver ce personnel?",
                                            currentId: item.personnel_id,
                                            actionSection: "personal",
                                          },
                                        })
                                      : dispatch({
                                          type: "ACTIVATE",
                                          payload: {
                                            actionText:
                                              "Êtes-vous sûr de vouloir Activer ce personnel?",
                                            currentId: item.personnel_id,
                                            actionSection: "personal",
                                          },
                                        });
                                  }}
                                  className={`${
                                    item.status
                                      ? "text-green-500 hover:text-green-700"
                                      : "text-gray-500 hover:text-green-700"
                                  } h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-green-500/10 cursor-pointer focus-visible:ring-2 focus-visible:ring-blue-500`}
                                >
                                  <i className="fa-solid fa-circle-check"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    {filteredPersonals.length === 0 && (
                      <tr>
                        <td
                          colSpan={6}
                          className={`px-6 py-14 text-center ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          <i
                            className="fas fa-users text-2xl mb-3 block opacity-40"
                            aria-hidden="true"
                          ></i>
                          Aucun membre du personnel ne correspond à votre
                          recherche.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              <div
                className={`mt-3 flex items-center justify-between rounded-lg border px-3 py-2 ${isDarkMode ? "border-[#30363D] bg-[#161B22]/60" : "border-gray-200 bg-gray-50/60"}`}
              >
                <div
                  className={`text-sm ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {filteredPersonals.length === 0
                    ? "Aucun membre du personnel trouvé"
                    : `${filteredPersonals.length} membre${filteredPersonals.length > 1 ? "s" : ""} affiché${filteredPersonals.length > 1 ? "s" : ""} sur ${updatePersonnalDataArr.length}`}
                </div>
              </div>
            </div>
          </div>

          {/* Activate dialog */}
          {state_actions.actionType === "activate" && (
            <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/65 p-3 backdrop-blur-[3px] sm:p-6">
              <div
                className={`${
                  isDarkMode
                    ? "bg-[#161B22] border-[#30363D] text-gray-100"
                    : "bg-white border-gray-200 text-gray-900"
                } rounded-2xl border shadow-2xl w-full max-w-md p-5 sm:p-6`}
              >
                <div className="text-base font-semibold tracking-tight mb-3">
                  Confirmer l'activation
                </div>
                <p
                  className={`${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  } mb-6 text-sm leading-relaxed`}
                >
                  {state_actions.actionText}
                </p>
                <div className="flex flex-wrap justify-end gap-2">
                  <button
                    disabled={mutationActivatePersonal.isPending}
                    onClick={() =>
                      dispatch({
                        type: "CANCEL",
                        payload: {
                          actionText: "",
                          currentId: "",
                          actionSection: "",
                        },
                      })
                    }
                    className={`px-4 py-2 rounded-lg ${
                      isDarkMode
                        ? "bg-transparent border border-[#30363D] text-gray-300 hover:bg-[#21262D]"
                        : "bg-transparent border border-gray-300 text-gray-600 hover:bg-gray-50"
                    } !rounded-button whitespace-nowrap ${
                      mutationActivatePersonal.isPending
                        ? "cursor-not-allowed opacity-60"
                        : ""
                    }`}
                  >
                    Annuler
                  </button>
                  <button
                    disabled={mutationActivatePersonal.isPending}
                    onClick={handleActivate}
                    className={`px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-600 text-white shadow-sm !rounded-button whitespace-nowrap ${
                      mutationActivatePersonal.isPending
                        ? "cursor-not-allowed opacity-70"
                        : ""
                    }`}
                  >
                    {mutationActivatePersonal.isPending ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="fas fa-spinner fa-spin"></i>
                        Activation en cours...
                      </span>
                    ) : (
                      "Confirmer l'activation"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Desactivate dialog */}
          {state_actions.actionType === "desactivate" && (
            <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/65 p-3 backdrop-blur-[3px] sm:p-6">
              <div
                className={`${
                  isDarkMode
                    ? "bg-[#161B22] border-[#30363D] text-gray-100"
                    : "bg-white border-gray-200 text-gray-900"
                } rounded-2xl border shadow-2xl w-full max-w-md p-5 sm:p-6`}
              >
                <div className="text-base font-semibold tracking-tight mb-3">
                  Confirmer la désactivation
                </div>
                <p
                  className={`${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  } mb-6 text-sm leading-relaxed`}
                >
                  {state_actions.actionText}
                </p>
                <div className="flex flex-wrap justify-end gap-2">
                  <button
                    disabled={mutationDesactivatePersonal.isPending}
                    onClick={() =>
                      dispatch({
                        type: "CANCEL",
                        payload: {
                          actionText: "",
                          currentId: "",
                          actionSection: "",
                        },
                      })
                    }
                    className={`px-4 py-2 rounded-lg ${
                      isDarkMode
                        ? "bg-transparent border border-[#30363D] text-gray-300 hover:bg-[#21262D]"
                        : "bg-transparent border border-gray-300 text-gray-600 hover:bg-gray-50"
                    } !rounded-button whitespace-nowrap ${
                      mutationDesactivatePersonal.isPending
                        ? "cursor-not-allowed opacity-60"
                        : ""
                    }`}
                  >
                    Annuler
                  </button>
                  <button
                    disabled={mutationDesactivatePersonal.isPending}
                    onClick={handleDesactivate}
                    className={`px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white shadow-sm !rounded-button whitespace-nowrap ${
                      mutationDesactivatePersonal.isPending
                        ? "cursor-not-allowed opacity-70"
                        : ""
                    }`}
                  >
                    {mutationDesactivatePersonal.isPending ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="fas fa-spinner fa-spin"></i>
                        Désactivation en cours...
                      </span>
                    ) : (
                      "Confirmer la désactivation"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ===== Détail personnel (au clic sur une ligne) ===== */}
          {showDetailModal && selectedPersonal && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/65 p-3 backdrop-blur-[3px] sm:p-6"
              onClick={closeDetailModal}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                className={`${
                  isDarkMode
                    ? "bg-[#161B22] border-[#30363D] text-gray-100"
                    : "bg-white border-gray-200 text-gray-900"
                } rounded-2xl border shadow-2xl w-full max-w-2xl max-h-[min(90vh,850px)] flex flex-col overflow-hidden`}
              >
                {/* Header */}
                <div
                  className={`px-4 sm:px-6 py-4 border-b flex-shrink-0 ${
                    isDarkMode ? "border-[#30363D]" : "border-gray-200"
                  } flex justify-between items-start`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        isDarkMode
                          ? "bg-blue-500/10 text-blue-400"
                          : "bg-blue-50 text-blue-600"
                      }`}
                    >
                      <i className="fas fa-user text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold tracking-tight leading-tight">
                        {capitalizeFirstLetter(selectedPersonal.nom)}{" "}
                        {capitalizeFirstLetter(selectedPersonal.postnom)}{" "}
                        {capitalizeFirstLetter(selectedPersonal.prenom)}
                      </h3>
                      <div className="flex flex-wrap items-center gap-2 mt-1.5">
                        {selectedPersonal.fonctionDesignation && (
                          <span
                            className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full ${
                              isDarkMode
                                ? "bg-gray-700 text-gray-300"
                                : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {selectedPersonal.fonctionDesignation}
                          </span>
                        )}
                        <span
                          className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full ${
                            selectedPersonal.status
                              ? isDarkMode
                                ? "bg-blue-900/40 text-blue-400"
                                : "bg-blue-100 text-blue-800"
                              : isDarkMode
                                ? "bg-red-900/40 text-red-400"
                                : "bg-red-100 text-red-800"
                          }`}
                        >
                          {selectedPersonal.status ? "Actif" : "Inactif"}
                        </span>
                        <span
                          className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full ${
                            selectedPersonal.personalAccount
                              ? isDarkMode
                                ? "bg-green-900/40 text-green-400"
                                : "bg-green-100 text-green-800"
                              : isDarkMode
                                ? "bg-gray-800 text-gray-400"
                                : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {selectedPersonal.personalAccount
                            ? "Compte système actif"
                            : "Sans compte système"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={closeDetailModal}
                    className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors flex-shrink-0 ${
                      isDarkMode
                        ? "text-gray-400 hover:bg-[#21262D] hover:text-gray-200"
                        : "text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    }`}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>

                {/* Body */}
                <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-5 space-y-6">
                  {/* Identité */}
                  <section>
                    <div className="flex items-center gap-2 mb-3">
                      <i
                        className={`fas fa-id-card text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      ></i>
                      <h4
                        className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Identité
                      </h4>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-3">
                      <DetailField
                        label="Genre"
                        value={
                          selectedPersonal.sexe === "M"
                            ? "Masculin"
                            : selectedPersonal.sexe === "F"
                              ? "Féminin"
                              : "-"
                        }
                        isDarkMode={isDarkMode}
                      />
                      <DetailField
                        label="Date de naissance"
                        value={selectedPersonal.date_naissance}
                        icon="fa-cake-candles"
                        isDarkMode={isDarkMode}
                      />
                      <DetailField
                        label="Lieu de naissance"
                        value={
                          selectedPersonal.lieu_naissance
                            ? capitalizeFirstLetter(
                                selectedPersonal.lieu_naissance,
                              )
                            : "-"
                        }
                        isDarkMode={isDarkMode}
                      />
                    </div>
                  </section>

                  <hr
                    className={
                      isDarkMode ? "border-[#30363D]" : "border-gray-200"
                    }
                  />

                  {/* Fonction */}
                  <section>
                    <div className="flex items-center gap-2 mb-3">
                      <i
                        className={`fas fa-briefcase text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      ></i>
                      <h4
                        className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Fonction
                      </h4>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-3">
                      <DetailField
                        label="Grade"
                        value={selectedPersonal.gradeDesignation}
                        isDarkMode={isDarkMode}
                      />
                      <DetailField
                        label="Domaine"
                        value={selectedPersonal.domaineDesignation}
                        isDarkMode={isDarkMode}
                      />
                      <DetailField
                        label="Fonction"
                        value={selectedPersonal.fonctionDesignation}
                        isDarkMode={isDarkMode}
                      />
                    </div>
                  </section>

                  <hr
                    className={
                      isDarkMode ? "border-[#30363D]" : "border-gray-200"
                    }
                  />

                  {/* Adresse */}
                  <section>
                    <div className="flex items-center gap-2 mb-3">
                      <i
                        className={`fas fa-map-marker-alt text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      ></i>
                      <h4
                        className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Adresse principale
                      </h4>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-3">
                      <DetailField
                        label="Avenue"
                        value={selectedPersonal.avenue}
                        isDarkMode={isDarkMode}
                      />
                      <DetailField
                        label="Quartier"
                        value={selectedPersonal.quartier}
                        isDarkMode={isDarkMode}
                      />
                      <DetailField
                        label="Commune"
                        value={selectedPersonal.commune}
                        isDarkMode={isDarkMode}
                      />
                      <DetailField
                        label="Ville"
                        value={selectedPersonal.ville}
                        isDarkMode={isDarkMode}
                      />
                    </div>
                  </section>

                  <hr
                    className={
                      isDarkMode ? "border-[#30363D]" : "border-gray-200"
                    }
                  />

                  {/* Contact */}
                  <section>
                    <div className="flex items-center gap-2 mb-3">
                      <i
                        className={`fas fa-address-book text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      ></i>
                      <h4
                        className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Contact
                      </h4>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3">
                      <DetailField
                        label="Email"
                        value={selectedPersonal.email}
                        icon="fa-envelope"
                        isDarkMode={isDarkMode}
                      />
                      <DetailField
                        label="Téléphone"
                        value={selectedPersonal.telephone}
                        icon="fa-phone"
                        isDarkMode={isDarkMode}
                      />
                    </div>
                  </section>
                </div>

                {/* Footer */}
                <div
                  className={`px-4 sm:px-6 py-3.5 border-t flex-shrink-0 ${
                    isDarkMode ? "border-[#30363D]" : "border-gray-200"
                  } flex justify-end gap-3`}
                >
                  <button
                    onClick={closeDetailModal}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors !rounded-button whitespace-nowrap ${
                      isDarkMode
                        ? "bg-transparent border border-[#30363D] text-gray-300 hover:bg-[#21262D]"
                        : "bg-transparent border border-gray-300 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    Fermer
                  </button>
                  <button
                    onClick={() => {
                      openEditForPersonal(selectedPersonal);
                      closeDetailModal();
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors shadow-sm !rounded-button whitespace-nowrap ${
                      isDarkMode
                        ? "bg-blue-600 hover:bg-blue-700"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    <span className="flex items-center justify-center gap-2">
                      <i className="fas fa-edit"></i>
                      Modifier
                    </span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Edit && Add personal Modal */}
          {(showEditModal || showAddModal) && (
            <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/65 p-3 backdrop-blur-[3px] sm:p-6">
              <div
                className={`${
                  isDarkMode
                    ? "bg-[#161B22] border-[#30363D] text-gray-100"
                    : "bg-white border-gray-200 text-gray-900"
                } rounded-2xl border shadow-2xl w-full max-w-4xl max-h-[min(92vh,900px)] flex flex-col overflow-hidden`}
              >
                {/* Header */}
                <div
                  className={`px-4 sm:px-6 py-4 border-b flex-shrink-0 ${
                    isDarkMode
                      ? "border-[#30363D] bg-[#161B22]"
                      : "border-gray-200 bg-white"
                  } flex justify-between items-center`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        isDarkMode
                          ? "bg-blue-500/10 text-blue-400"
                          : "bg-blue-50 text-blue-600"
                      }`}
                    >
                      <i
                        className={
                          showEditModal
                            ? "fas fa-user-edit"
                            : "fas fa-user-plus"
                        }
                      ></i>
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold tracking-tight leading-tight">
                        {showEditModal
                          ? "Modifier personnel"
                          : "Ajouter un nouveau personnel"}
                      </h3>
                      <p
                        className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      >
                        Les champs marqués d'un * sont obligatoires
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      showEditModal
                        ? setShowEditModal(false)
                        : setShowAddModal(false);
                    }}
                    className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
                      isDarkMode
                        ? "text-gray-400 hover:bg-[#21262D] hover:text-gray-200"
                        : "text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    }`}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>

                {/* Form */}
                <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-5 space-y-6">
                  {/* Section: Identité */}
                  <section>
                    <div className="flex items-center gap-2 mb-3">
                      <i
                        className={`fas fa-id-card text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      ></i>
                      <h4
                        className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Identité
                      </h4>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                      <div>
                        <label
                          htmlFor="nom"
                          className={`block text-xs font-medium mb-1.5 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Nom <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="nom"
                          name="nom"
                          required
                          value={
                            showEditModal ? formEditData.nom : formNewData.nom
                          }
                          onChange={handleChange}
                          className={`block w-full min-h-10 border rounded-lg px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100 placeholder:text-gray-500"
                              : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                          }`}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="postnom"
                          className={`block text-xs font-medium mb-1.5 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Post-Nom <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="postnom"
                          name="postnom"
                          required
                          value={
                            showEditModal
                              ? formEditData.postnom
                              : formNewData.postnom
                          }
                          onChange={handleChange}
                          className={`block w-full min-h-10 border rounded-lg px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100 placeholder:text-gray-500"
                              : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                          }`}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="prenom"
                          className={`block text-xs font-medium mb-1.5 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Prénom <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="prenom"
                          name="prenom"
                          required
                          value={
                            showEditModal
                              ? formEditData.prenom
                              : formNewData.prenom
                          }
                          onChange={handleChange}
                          className={`block w-full min-h-10 border rounded-lg px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100 placeholder:text-gray-500"
                              : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                          }`}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
                      <div>
                        <label
                          htmlFor="genre"
                          className={`block text-xs font-medium mb-1.5 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Genre <span className="text-red-500">*</span>
                        </label>
                        <select
                          id="genre"
                          name="genre"
                          required
                          value={
                            showEditModal
                              ? formEditData.genre
                              : formNewData.genre
                          }
                          onChange={handleChange}
                          className={`block w-full min-h-10 border rounded-lg px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100 placeholder:text-gray-500"
                              : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                          }`}
                        >
                          <option value="">Sélectionner</option>
                          <option value="M">Masculin</option>
                          <option value="F">Féminin</option>
                        </select>
                      </div>
                      <div>
                        <label
                          htmlFor="lieuxNaissance"
                          className={`block text-xs font-medium mb-1.5 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Lieu de naissance{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="lieuxNaissance"
                          name="lieuxNaissance"
                          required
                          value={
                            showEditModal
                              ? formEditData.lieu_naissance
                              : formNewData.lieu_naissance
                          }
                          onChange={(e) =>
                            showEditModal
                              ? setFormEditData((prev) => ({
                                  ...prev,
                                  lieu_naissance: e.target.value,
                                }))
                              : setFormNewData((prev) => ({
                                  ...prev,
                                  lieu_naissance: e.target.value,
                                }))
                          }
                          className={`block w-full min-h-10 border rounded-lg px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100 placeholder:text-gray-500"
                              : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                          }`}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="date_naissance"
                          className={`block text-xs font-medium mb-1.5 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Date de naissance{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          id="date_naissance"
                          name="date_naissance"
                          required
                          value={
                            showEditModal
                              ? formEditData.date_naissance
                              : moment(formNewData.date_naissance).format(
                                  "DD-MM-YYYY",
                                )
                          }
                          onChange={handleChange}
                          className={`block w-full min-h-10 border rounded-lg px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100 placeholder:text-gray-500"
                              : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                          }`}
                        />
                      </div>
                    </div>
                  </section>

                  <hr
                    className={
                      isDarkMode ? "border-[#30363D]" : "border-gray-200"
                    }
                  />

                  {/* Section: Adresse */}
                  <section>
                    <div className="flex items-center gap-2 mb-3">
                      <i
                        className={`fas fa-map-marker-alt text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      ></i>
                      <h4
                        className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Adresse principale
                      </h4>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-6 gap-5">
                      <div className="sm:col-span-4">
                        <label
                          htmlFor="avenue"
                          className={`block text-xs font-medium mb-1.5 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Avenue <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="avenue"
                          id="avenue"
                          required
                          value={
                            showEditModal
                              ? formEditData.avenue
                              : formNewData.avenue
                          }
                          onChange={handleChange}
                          className={`block w-full min-h-10 border rounded-lg px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100 placeholder:text-gray-500"
                              : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                          }`}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label
                          htmlFor="quartier"
                          className={`block text-xs font-medium mb-1.5 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Quartier <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="quartier"
                          id="quartier"
                          required
                          value={
                            showEditModal
                              ? formEditData.quartier
                              : formNewData.quartier
                          }
                          onChange={handleChange}
                          className={`block w-full min-h-10 border rounded-lg px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100 placeholder:text-gray-500"
                              : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                          }`}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label
                          htmlFor="commune"
                          className={`block text-xs font-medium mb-1.5 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Commune <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="commune"
                          id="commune"
                          required
                          value={
                            showEditModal
                              ? formEditData.commune
                              : formNewData.commune
                          }
                          onChange={handleChange}
                          className={`block w-full min-h-10 border rounded-lg px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100 placeholder:text-gray-500"
                              : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                          }`}
                        />
                      </div>
                      <div className="sm:col-span-4">
                        <label
                          htmlFor="ville"
                          className={`block text-xs font-medium mb-1.5 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Ville <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="ville"
                          id="ville"
                          required
                          value={
                            showEditModal
                              ? formEditData.ville
                              : formNewData.ville
                          }
                          onChange={handleChange}
                          className={`block w-full min-h-10 border rounded-lg px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100 placeholder:text-gray-500"
                              : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                          }`}
                        />
                      </div>
                    </div>
                  </section>

                  <hr
                    className={
                      isDarkMode ? "border-[#30363D]" : "border-gray-200"
                    }
                  />

                  {/* Section: Fonction */}
                  <section>
                    <div className="flex items-center gap-2 mb-3">
                      <i
                        className={`fas fa-briefcase text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      ></i>
                      <h4
                        className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Fonction
                      </h4>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                      <div>
                        <label
                          htmlFor="grade"
                          className={`block text-xs font-medium mb-1.5 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Grade <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="grade"
                          id="grade"
                          required
                          value={
                            showEditModal
                              ? formEditData.grade_id
                              : formNewData.grade_id
                          }
                          onChange={(e) =>
                            showEditModal
                              ? setFormEditData((prev) => ({
                                  ...prev,
                                  grade_id:
                                    e.target.value === ""
                                      ? ""
                                      : Number(e.target.value),
                                }))
                              : setFormNewData((prev) => ({
                                  ...prev,
                                  grade_id:
                                    e.target.value === ""
                                      ? ""
                                      : Number(e.target.value),
                                }))
                          }
                          className={`block w-full h-10 border rounded-lg px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100 placeholder:text-gray-500"
                              : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                          }`}
                        >
                          <option value="">Sélectionner le grade</option>
                          {gradeDataArr.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.designation.toUpperCase()}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label
                          htmlFor="domaine"
                          className={`block text-xs font-medium mb-1.5 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Domaine <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="domaine"
                          id="domaine"
                          required
                          value={
                            showEditModal
                              ? formEditData.domaine_id
                              : formNewData.domaine_id
                          }
                          onChange={(e) =>
                            showEditModal
                              ? setFormEditData((prev) => ({
                                  ...prev,
                                  domaine_id:
                                    e.target.value === ""
                                      ? null
                                      : parseInt(e.target.value),
                                }))
                              : setFormNewData((prev) => ({
                                  ...prev,
                                  domaine_id:
                                    e.target.value === ""
                                      ? null
                                      : parseInt(e.target.value),
                                }))
                          }
                          className={`block w-full h-10 border rounded-lg px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100 placeholder:text-gray-500"
                              : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                          }`}
                        >
                          <option value="">Sélectionner le domaine</option>
                          {domainDataArr.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.designation.toUpperCase()}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label
                          htmlFor="fonction"
                          className={`block text-xs font-medium mb-1.5 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Fonction <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="fonction"
                          id="fonction"
                          required
                          value={
                            showEditModal
                              ? formEditData.fonction_id
                              : formNewData.fonction_id
                          }
                          onChange={(e) =>
                            showEditModal
                              ? setFormEditData((prev) => ({
                                  ...prev,
                                  fonction_id:
                                    e.target.value === ""
                                      ? ""
                                      : Number(e.target.value),
                                }))
                              : setFormNewData((prev) => ({
                                  ...prev,
                                  fonction_id:
                                    e.target.value === ""
                                      ? ""
                                      : Number(e.target.value),
                                }))
                          }
                          className={`block w-full h-10 border rounded-lg px-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100 placeholder:text-gray-500"
                              : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                          }`}
                        >
                          <option value="">Sélectionner la fonction</option>
                          {functionDataArr.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.designation.toUpperCase()}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </section>

                  <hr
                    className={
                      isDarkMode ? "border-[#30363D]" : "border-gray-200"
                    }
                  />

                  {/* Section: Contact */}
                  <section>
                    <div className="flex items-center gap-2 mb-3">
                      <i
                        className={`fas fa-address-book text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                      ></i>
                      <h4
                        className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Contact
                      </h4>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label
                          htmlFor="email"
                          className={`block text-xs font-medium mb-1.5 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          id="email"
                          required
                          value={
                            showEditModal
                              ? formEditData.email
                              : formNewData.email
                          }
                          onChange={handleChange}
                          className={`block w-full min-h-10 border rounded-lg px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100 placeholder:text-gray-500"
                              : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                          }`}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="telephone"
                          className={`block text-xs font-medium mb-1.5 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          Téléphone <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          name="telephone"
                          id="telephone"
                          required
                          value={
                            showEditModal
                              ? formEditData.telephone
                              : formNewData.telephone
                          }
                          onChange={handleChange}
                          className={`block w-full min-h-10 border rounded-lg px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
                            isDarkMode
                              ? "bg-[#0D1117] border-[#30363D] text-gray-100 placeholder:text-gray-500"
                              : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                          }`}
                        />
                      </div>
                    </div>
                  </section>
                </div>

                {/* Footer */}
                <div
                  className={`px-4 sm:px-6 py-3.5 border-t flex-shrink-0 ${
                    isDarkMode
                      ? "border-[#30363D] bg-[#161B22]"
                      : "border-gray-200 bg-white"
                  } flex justify-end gap-3`}
                >
                  <button
                    disabled={
                      mutationUpdatePersonal.isPending ||
                      mutationCreatePersonal.isPending
                    }
                    onClick={() => {
                      showEditModal
                        ? setShowEditModal(false)
                        : setShowAddModal(false);
                      showEditModal
                        ? setFormEditData({
                            id: "",
                            nom: "",
                            postnom: "",
                            prenom: "",
                            genre: "",
                            ville: "",
                            commune: "",
                            quartier: "",
                            avenue: "",
                            telephone: "",
                            uid: "12345688",
                            fonction_id: "",
                            grade_id: "",
                            date_naissance: "",
                            lieu_naissance: "",
                            domaine_id: "",
                            email: "",
                          })
                        : setFormNewData({
                            nom: "",
                            postnom: "",
                            prenom: "",
                            genre: "",
                            ville: "",
                            commune: "",
                            quartier: "",
                            avenue: "",
                            telephone: "",
                            uid: "12345688",
                            fonction_id: "",
                            grade_id: "",
                            date_naissance: "",
                            lieu_naissance: "",
                            domaine_id: "",
                            email: "",
                          });
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors !rounded-button whitespace-nowrap ${
                      isDarkMode
                        ? "bg-transparent border border-[#30363D] text-gray-300 hover:bg-[#21262D]"
                        : "bg-transparent border border-gray-300 text-gray-600 hover:bg-gray-50"
                    } ${
                      mutationUpdatePersonal.isPending ||
                      mutationCreatePersonal.isPending
                        ? "cursor-not-allowed opacity-60"
                        : ""
                    }`}
                  >
                    Annuler
                  </button>

                  <button
                    disabled={
                      mutationUpdatePersonal.isPending ||
                      mutationCreatePersonal.isPending
                    }
                    onClick={() => {
                      showEditModal
                        ? handleUpdatePersonal()
                        : handleCreatePersonal();
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors shadow-sm !rounded-button whitespace-nowrap ${
                      isDarkMode
                        ? "bg-blue-600 hover:bg-blue-700"
                        : "bg-blue-600 hover:bg-blue-700"
                    } ${
                      mutationUpdatePersonal.isPending ||
                      mutationCreatePersonal.isPending
                        ? "cursor-not-allowed opacity-70"
                        : ""
                    }`}
                  >
                    {mutationUpdatePersonal.isPending ||
                    mutationCreatePersonal.isPending ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="fas fa-spinner fa-spin"></i>
                        Chargement...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <i className="fas fa-check"></i>
                        {showEditModal ? "Mettre à jour" : "Ajouter"}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ===== NOTIFICATION ===== */}
          {notification && (
            <div
              className={`fixed bottom-4 text-[14px] right-4 px-6 py-3 z-[60] rounded-lg text-white shadow-lg transition-all duration-500 ${
                notification.type === "success" ? "bg-green-500" : "bg-red-500"
              }`}
            >
              {notification.message}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Personal;
